import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Download, Calendar, Maximize2, MessageSquare, Mail, Star, Paperclip, Check, AlertTriangle, Award, Send, Eye, MousePointerClick, BarChart3, HelpCircle, Database, UserX } from 'lucide-react'
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts'
import { ChartContainer, type ChartConfig } from '@/shared/components/ui/chart'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useCampania, useCategorizacion } from '@/modules/multichannel/hooks/useCampanias'
import { useReportePorCampania, useReporteWhatsAppPorCampania } from '@/modules/cronograma/hooks/useReportes'
import { ConversionFunnel } from '@/modules/multichannel/components/ConversionFunnel'
import { RedirectBanner } from '@/modules/multichannel/components/RedirectBanner'
import { SuperMetricCard, GaugeChart, BenchmarkBarChart, catColorMap } from '@/modules/multichannel/components/ReportCharts'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { useAuth } from '@/auth'
import { canUpdateReports } from '@/shared/lib/utils'

const BENCHMARKS = {
  entrega: 95,
  aperturas: 25,
  clics: 3,
}

export function ReporteDetalle() {
  const { id } = useParams<{ id: string }>()
  const campaniaId = Number(id)
  const navigate = useNavigate()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [showBenchmarkInfo, setShowBenchmarkInfo] = useState(false)
  const { setOverride, clearOverride } = useBreadcrumb()
  const { user } = useAuth()
  const showActualizado = canUpdateReports(user)

  // Obtener el reporte por ID de campaña (no por ID del reporte)
  const { data: reporte, isLoading, error } = useReportePorCampania(campaniaId, true)

  // Verificar si existe reporte de WhatsApp para mostrar botón de enlace
  const { data: reporteWhatsApp } = useReporteWhatsAppPorCampania(campaniaId, true)

  // Obtener datos de la campaña para el HTML
  const { data: campania } = useCampania(campaniaId)

  // Obtener score de categorización
  const { data: categorizacionData } = useCategorizacion(
    reporte ? [reporte.campania_id] : []
  )
  const scoreData = categorizacionData?.[String(reporte?.campania_id)]

  // Breadcrumb: mostrar nombre del reporte en vez del ID
  useEffect(() => {
    if (reporte?.titulo_campana) {
      setOverride(`/multichannel/reporte/${id}`, `Reporte: ${reporte.titulo_campana}`)
    }
    return () => clearOverride(`/multichannel/reporte/${id}`)
  }, [reporte?.titulo_campana, id, setOverride, clearOverride])

  const formatPercentage = (num: number) => {
    return `${num.toFixed(2)}%`
  }



  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-[400px]" />
            <Skeleton className="h-4 w-[300px]" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    )
  }

  if (error || !reporte) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-2xl font-bold mb-2">Reporte no encontrado</h2>
        <p className="text-muted-foreground mb-6">
          No se pudo cargar la información de este reporte
        </p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
      </div>
    )
  }

  // Fortalezas y áreas de mejora
  const fortalezas: string[] = []
  const mejoras: string[] = []

  if (reporte.tasa_entrega >= BENCHMARKS.entrega) fortalezas.push('Excelente entregabilidad')
  else mejoras.push('Revisar configuración de envío')

  if (reporte.tasa_aperturas >= BENCHMARKS.aperturas) fortalezas.push('Asuntos muy efectivos')
  else mejoras.push('Optimizar líneas de asunto')

  if (reporte.tasa_clics >= BENCHMARKS.clics) fortalezas.push('Contenido relevante')
  else mejoras.push('Mejorar llamadas a la acción')

  const scoreCalificacion = scoreData?.calificacion?.toUpperCase() || null
  const scoreColors = catColorMap[scoreCalificacion || 'C'] || catColorMap['C']

  return (
    <div className="space-y-6">
      {/* Page Heading */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-muted-foreground" />
            Reporte de Campaña
          </h1>
          {reporte.completado && reporte.dias_recopilacion && (
            <Badge variant="outline" className="text-green-600 border-green-300">
              <Check className="h-3 w-3 mr-1" />
              Completado ({reporte.dias_recopilacion} días)
            </Badge>
          )}
          {showActualizado && (
            reporte.actualizado ? (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Actualizado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Pendiente de actualizar
              </Badge>
            )
          )}
        </div>
        <div className="flex items-center gap-2">
          {reporte.habilitar_boton_excel && reporte.url_excel && (
            <a
              href={reporte.url_excel}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Excel
            </a>
          )}
        </div>
      </div>

      {/* Header - Row 1: Título (col-8) + Cronograma (col-4) */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* Título + Score */}
        <Card
          className="md:col-span-8 border-gray-200 dark:border-gray-700"
          style={{ background: `linear-gradient(to right, ${scoreColors.hex}20, transparent 60%)` }}
        >
          <CardContent className="h-full flex items-center py-4">
            <div className="flex items-center gap-3 flex-1">
              <Calendar className="h-8 w-8 text-muted-foreground shrink-0" />
              <div>
                <h2 className="text-2xl font-bold">{reporte.titulo_campana}</h2>
                <p className="text-sm text-muted-foreground">
                  Campaña Mailing{campania?.marca_principal_nombre ? ` • ${campania.marca_principal_nombre}` : ''}
                </p>
                {reporteWhatsApp && (
                  <div className="mt-1.5">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/multichannel/reporte-whatsapp/${campaniaId}`}>
                        <MessageSquare className="mr-1 h-3.5 w-3.5 text-green-600" />
                        Reporte WhatsApp
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {scoreData && (
              <div className="flex flex-col items-center shrink-0">
                <Badge className={`${scoreColors.bg} ${scoreColors.text} text-[10px] mb-1 gap-1`}>
                  {scoreCalificacion && scoreColors.icon}
                  {scoreCalificacion ? scoreColors.label : 'Sin datos'}
                </Badge>
                <div className="bg-background rounded-full p-0.5 shadow">
                  <div
                    className={`rounded-full flex items-center justify-center ${scoreColors.bg} ${scoreColors.text}`}
                    style={{ width: '44px', height: '44px' }}
                  >
                    <span className="text-sm font-bold">{scoreData.calificacion}</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold mt-0.5">{scoreData.score_final}/100</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fechas de envío */}
        <Card className="md:col-span-4">
          <CardContent className="pt-6 flex items-center h-full">
            <div className="flex-1 space-y-2">
              {reporte.cronogramas && reporte.cronogramas.length > 0 ? (
                reporte.cronogramas.map((cronograma) => (
                  <div key={cronograma.id}>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {cronograma.tipo_envio_nombre && `${cronograma.tipo_envio_nombre} • `}
                      {cronograma.fecha_hora ? new Intl.DateTimeFormat('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(cronograma.fecha_hora)) : '-'}
                      {cronograma.fecha_hora && ` • ${new Intl.DateTimeFormat('es-EC', { hour: '2-digit', minute: '2-digit' }).format(new Date(cronograma.fecha_hora))}`}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Sin información de envío</p>
              )}
              {reporte.completado && reporte.dias_recopilacion && (
                <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                  <Check className="h-3 w-3 mr-1" />
                  Completado ({reporte.dias_recopilacion} días)
                </Badge>
              )}
            </div>
            <div className="ml-3 rounded-full bg-muted p-3">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banner de redirección */}
      {reporte.reporte_relacionado_id && (
        <RedirectBanner campaniaOrigenNombre={reporte.titulo_campana} />
      )}

      {/* Métricas principales */}
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
        <div
          className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ background: 'linear-gradient(to right, #57a6d820, transparent 60%)' }}
        >
          <div className="px-4 py-3">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Base Total</p>
            <div className="flex items-center gap-3">
              <Database className="h-10 w-10 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0 text-right">
                <div className="text-2xl font-bold text-foreground leading-tight">
                  {new Intl.NumberFormat('es-EC').format(reporte.base)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {new Intl.NumberFormat('es-EC').format(reporte.entrega)} entregados
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Conversión: {reporte.tasa_clics.toFixed(2)}%
                </p>
              </div>
            </div>
          </div>
        </div>
        <SuperMetricCard
          icon={<Send className="h-10 w-10" />}
          label="Entregados"
          percentage={reporte.tasa_entrega}
          count={reporte.entrega}
          benchmark={BENCHMARKS.entrega}
          category={reporte.categoria_entrega}
        />
        <SuperMetricCard
          icon={<Eye className="h-10 w-10" />}
          label="Aperturas"
          percentage={reporte.tasa_aperturas}
          count={reporte.aperturas_unicas}
          benchmark={BENCHMARKS.aperturas}
          category={reporte.categoria_aperturas}
        />
        <SuperMetricCard
          icon={<MousePointerClick className="h-10 w-10" />}
          label="Clics"
          percentage={reporte.tasa_clics}
          count={reporte.clics_unicos}
          benchmark={BENCHMARKS.clics}
          category={reporte.categoria_clics}
        />
        <SuperMetricCard
          icon={<UserX className="h-10 w-10" />}
          label="Desuscripciones"
          percentage={reporte.tasa_unsub}
          count={reporte.unsub}
        />
      </div>

      {/* Embudo de Conversión y Vista Previa del HTML */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Embudo de Conversión */}
        <ConversionFunnel
          base={reporte.base}
          entrega={reporte.entrega}
          aperturas={reporte.aperturas_unicas}
          clics={reporte.clics_unicos}
          tasa_entrega={reporte.tasa_entrega}
          tasa_aperturas={reporte.tasa_aperturas}
          tasa_clics={reporte.tasa_clics}
          calificacion={scoreCalificacion}
        />

        {/* Vista previa del contenido HTML */}
        {campania?.contenido_html && (
          <Card className="shadow">
            <CardHeader className="py-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold">Vista Previa del Email</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <Maximize2 className="mr-2 h-3 w-3" />
                  Expandir
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3" style={{ height: '550px' }}>
              <div className="border rounded-lg overflow-hidden bg-white h-full">
                <iframe
                  srcDoc={campania.contenido_html}
                  className="w-full h-full"
                  title="Vista previa del email"
                  sandbox="allow-same-origin allow-popups allow-popups-to-navigate-by-user-activation"
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Base Dirigida + Gauge Charts */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Base Dirigida</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{reporte.base_dirigida}</p>
            <div className="mt-4">
              <Badge variant="outline" className="text-xs">
                Conversión: {formatPercentage(reporte.tasa_clics)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <GaugeChart
          value={reporte.tasa_entrega}
          benchmark={BENCHMARKS.entrega}
          label="Tasa de Entrega"
          category={reporte.categoria_entrega}
          icon={<Send className="h-3.5 w-3.5 text-muted-foreground" />}
        />
        <GaugeChart
          value={reporte.tasa_aperturas}
          benchmark={BENCHMARKS.aperturas}
          label="Tasa de Aperturas"
          category={reporte.categoria_aperturas}
          icon={<Eye className="h-3.5 w-3.5 text-muted-foreground" />}
        />
        <GaugeChart
          value={reporte.tasa_clics}
          benchmark={BENCHMARKS.clics}
          label="Tasa de Clics"
          category={reporte.categoria_clics}
          icon={<MousePointerClick className="h-3.5 w-3.5 text-muted-foreground" />}
        />
      </div>

      {/* Simulación Bandeja de Entrada */}
      {reporte.asuntos && reporte.asuntos.length > 0 && (
        <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden">
          {/* Header bandeja */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Bandeja de entrada - Vista previa del destinatario</span>
            </div>
            <Badge variant="outline" className="text-xs border-gray-300 dark:border-gray-600">
              <Mail className="h-3 w-3 mr-1" /> {reporte.asuntos.length} correo{reporte.asuntos.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          {/* Email rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {reporte.asuntos.map((asunto, idx) => (
              <div
                key={asunto.id ?? idx}
                className={`px-4 py-2.5 flex items-center gap-3 ${
                  idx === 0
                    ? 'bg-white dark:bg-gray-900/50 font-semibold'
                    : 'bg-gray-50/50 dark:bg-gray-900/20'
                }`}
              >
                <Star className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
                <Paperclip className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                <span className={`text-sm shrink-0 ${idx === 0 ? 'font-bold' : 'font-medium'} text-foreground`}>
                  {campania?.remitente || 'Remitente'}
                </span>
                <div className="flex-1 min-w-0 flex items-baseline gap-2">
                  <span className={`text-sm truncate ${idx === 0 ? 'font-bold' : 'font-medium'} text-foreground`}>
                    {asunto.asunto}
                  </span>
                  {asunto.preencabezado && (
                    <span className="text-sm text-muted-foreground truncate">
                      - {asunto.preencabezado}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {idx === 0 ? 'Ahora' : `${10 + idx}:${String(45 - idx * 5).padStart(2, '0')}`}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Análisis de Benchmark */}
      <div className="grid gap-6 md:grid-cols-12">
        {/* Bar Charts comparativos */}
        <Card className="md:col-span-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <BarChart3 className="h-5 w-5 shrink-0" />
              <span>Análisis Comparativo de Benchmark</span>
              <button
                onClick={() => setShowBenchmarkInfo(!showBenchmarkInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </CardTitle>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showBenchmarkInfo ? 'max-h-8 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-muted-foreground">
                Rendimiento de la campaña vs estándares de la industria
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <BenchmarkBarChart
                tasa={reporte.tasa_entrega}
                benchmark={BENCHMARKS.entrega}
                label="Tasa de Entrega"
                category={reporte.categoria_entrega}
                icon={<Send className="h-4 w-4 text-muted-foreground" />}
              />
              <BenchmarkBarChart
                tasa={reporte.tasa_aperturas}
                benchmark={BENCHMARKS.aperturas}
                label="Tasa de Aperturas"
                category={reporte.categoria_aperturas}
                icon={<Eye className="h-4 w-4 text-muted-foreground" />}
              />
              <BenchmarkBarChart
                tasa={reporte.tasa_clics}
                benchmark={BENCHMARKS.clics}
                label="Tasa de Clics"
                category={reporte.categoria_clics}
                icon={<MousePointerClick className="h-4 w-4 text-muted-foreground" />}
              />
            </div>
          </CardContent>
        </Card>

        {/* Score General + Análisis + Conclusión */}
        <Card className="md:col-span-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Score General
              </CardTitle>
              <Badge className={`${scoreColors.bg} ${scoreColors.text} gap-1`}>
                {scoreCalificacion && scoreColors.icon}
                {scoreCalificacion ? scoreColors.label : 'Sin datos'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Radial chart de score */}
            <ChartContainer
              config={{
                score: { label: 'Score', color: scoreColors.hex },
              } satisfies ChartConfig}
              className="mx-auto aspect-square max-h-[160px]"
            >
              <RadialBarChart
                data={[{ score: scoreData?.score_final ?? 0, fill: scoreColors.hex }]}
                startAngle={90}
                endAngle={90 - ((scoreData?.score_final ?? 0) / 100) * 360}
                outerRadius={70}
                innerRadius={58}
              >
                <RadialBar dataKey="score" background cornerRadius={10} />
                <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 8} className="fill-foreground text-2xl font-bold">
                              {scoreData?.calificacion || '-'}
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 14} className="fill-muted-foreground text-xs">
                              {scoreData?.score_final ?? '-'}/100
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </PolarRadiusAxis>
              </RadialBarChart>
            </ChartContainer>

            {/* Fortalezas */}
            {fortalezas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-green-600" />
                  Fortalezas
                </h4>
                <ul className="space-y-1">
                  {fortalezas.map((f) => (
                    <li key={f} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-green-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Áreas de mejora */}
            {mejoras.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Áreas de Mejora
                </h4>
                <ul className="space-y-1">
                  {mejoras.map((m) => (
                    <li key={m} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Conclusión */}
            {reporte.conclusion && (
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium mb-1.5">Conclusión</h4>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                  {reporte.conclusion}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Modal de Vista Previa Expandida */}
      {campania?.contenido_html && (
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
            <DialogHeader className="p-6 pb-4">
              <DialogTitle>Vista Previa del Email</DialogTitle>
              <DialogDescription>
                Previsualización completa del contenido HTML de la campaña
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6">
              <div className="border rounded-lg overflow-hidden bg-white">
                <iframe
                  srcDoc={campania.contenido_html}
                  className="w-full h-[70vh]"
                  title="Vista previa del email expandida"
                  sandbox="allow-same-origin allow-popups allow-popups-to-navigate-by-user-activation"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
